import _ from "lodash";
import client from "lib/apolloClient/client";

import {
  getCourseCalendarDetailsQuery,
  getCalendarEventsQuery,
  getSchoolCalendarEventsQuery,
  getCalendarEventsTrimmedQuery,
  getUserProjectGroupYearGroupsQuery,
  getCalendarUidQuery,
  getCalendarEventsDataQuery,
  getCalendarEventsParticipantDataQuery,
  getCalendarEventDetailsQuery,
} from "./MyCalendarQuery";
import { STUDENT_USER } from "Constants/stringConstants";
import {
  getDateMomentObject,
  getISOString,
  formatDate,
} from "Utils/DateTimeHelpers";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import { getStartWeekOfMonth, getEndWeekOfMonth } from "./Utils";

export const getCourseCalendarDetailsFromCache = ({
  id,
  type,
  filters,
  enabledModulesFilters,
  aclControlParameterFilter,
}) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getCourseCalendarDetailsQuery,
      variables: {
        id,
        type,
        filters,
        enabledModulesFilters,
        aclControlParameterFilter,
      },
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getCalendarEventsFromCache = ({
  id,
  type,
  filters,
  statusWiseProjectFilters,
}) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getCalendarEventsQuery,
      variables: {
        id,
        type,
        filters,
        statusWiseProjectFilters,
      },
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getCalendarEventsTrimmedFromCache = ({ id, type, filters }) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getCalendarEventsTrimmedQuery,
      variables: {
        id,
        type,
        filters,
      },
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getSchoolCalendarEventsFromCache = ({
  rangeBeginStartDate,
  rangeEndStartDate,
  filters,
}) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getSchoolCalendarEventsQuery,
      variables: {
        rangeBeginStartDate,
        rangeEndStartDate,
        filters,
      },
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return _.get(queryData, "school", {});
  else return {};
};

export const getUserProjectGroupYearGroupsFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getUserProjectGroupYearGroupsQuery,
      variables,
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const getCalendarUidFromCache = ({ id, type, studentId }) => {
  try {
    const queryData = client.readQuery({
      query: getCalendarUidQuery,
      variables: {
        id,
        type,
        studentId,
      },
    });

    return _.get(queryData, "node") || {};
  } catch (e) {
    return {};
  }
};

export const readCalendarEventsFromCache = ({
  userId,
  userEntityType,
  selectedChildId,
  isUserParent,
  filters,
  startDate,
  endDate,
  statusWiseProjectFilters,
}) => {
  const formattedStartDate = getISOString(
    getDateMomentObject(startDate).startOf("day")
  );

  const formattedEndDate = getISOString(
    getDateMomentObject(endDate).endOf("day")
  );

  const variables = {
    id: isUserParent ? selectedChildId : userId,
    type: isUserParent ? STUDENT_USER : userEntityType,
    filters: {
      ...filters,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    },
    statusWiseProjectFilters,
  };

  try {
    const cachedData = client.readQuery({
      query: getCalendarEventsDataQuery,
      variables,
    });
    let cachedCalendarEvents = [];
    if (cachedData?.node?.calendarEvents) {
      cachedCalendarEvents = _.map(cachedData?.node?.calendarEvents, event => {
        const { item } = event;
        if (
          userEntityType == STUDENT_USER &&
          event?.item?.__typename === "ProjectTaskResponse"
        ) {
          return {
            ...event,
            item: {
              ...item,
              deadline: item?.task,
            },
          };
        }
        return event;
      });
    }
    return cachedCalendarEvents;
  } catch (error) {
    return {};
  }
};

export const updateCalendarEventsCache = ({
  userId,
  userEntityType,
  selectedChildId,
  isUserParent,
  filters,
  startDate,
  endDate,
  statusWiseProjectFilters,
  calendarData,
}) => {
  const formattedStartDate = getISOString(
    getDateMomentObject(startDate).startOf("day")
  );

  const formattedEndDate = getISOString(
    getDateMomentObject(endDate).endOf("day")
  );

  const variables = {
    id: isUserParent ? selectedChildId : userId,
    type: isUserParent ? STUDENT_USER : userEntityType,
    filters: {
      ...filters,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    },
    statusWiseProjectFilters,
  };

  client.writeQuery({
    query: getCalendarEventsDataQuery,
    variables,
    data: calendarData,
  });
};

export const fetchRemainingQuarters = async ({
  quartersForAcademicYear,
  currentQuarter,
  filters,
  statusWiseProjectFilters,
  userId,
  userEntityType,
  selectedChildId,
  isUserParent,
  academicStartDate,
  academicEndDate,
}) => {
  let mergedCalendarEvents = [];
  const {
    startDate: currentQuarterStartDate,
    endDate: currentQuarterEndDate,
  } = currentQuarter;

  const currentQuarterKey = `${currentQuarterStartDate}_${currentQuarterEndDate}`;

  const currentQuarterData = client.readQuery({
    query: getCalendarEventsDataQuery,
    variables: {
      id: isUserParent ? selectedChildId : userId,
      type: isUserParent ? STUDENT_USER : userEntityType,
      filters: {
        ...filters,
        startDate: getISOString(
          getDateMomentObject(currentQuarterStartDate).startOf("day")
        ),
        endDate: getISOString(
          getDateMomentObject(currentQuarterEndDate).endOf("day")
        ),
        calendarIds: _.uniq(filters?.calendarIds || []),
      },
      statusWiseProjectFilters,
    },
  });

  if (currentQuarterData?.node?.calendarEvents) {
    const currentQuarterCalendarEvents = _.map(
      currentQuarterData?.node?.calendarEvents,
      event => {
        const { item } = event;
        if (
          userEntityType === STUDENT_USER &&
          event?.item?.__typename === "ProjectTaskResponse"
        ) {
          return {
            ...event,
            item: {
              ...item,
              deadline: item?.task,
            },
          };
        }
        return event;
      }
    );
    mergedCalendarEvents = [...currentQuarterCalendarEvents];
  }

  const quarterQueryPromises = [];

  for (const quarter of quartersForAcademicYear) {
    const quarterKey = `${quarter.startDate}_${quarter.endDate}`;

    if (quarterKey === currentQuarterKey) {
      continue;
    }

    const { startDate: quarterStartDate, endDate: quarterEndDate } = quarter;
    const startDate = getISOString(
      getDateMomentObject(quarterStartDate).startOf("day")
    );
    const endDate = getISOString(
      getDateMomentObject(quarterEndDate).endOf("day")
    );

    quarterQueryPromises.push(
      client.query({
        query: getCalendarEventsDataQuery,
        variables: {
          id: isUserParent ? selectedChildId : userId,
          type: isUserParent ? STUDENT_USER : userEntityType,
          filters: { ...filters, startDate, endDate },
          statusWiseProjectFilters,
        },
        fetchPolicy: "network-only",
        context: {
          useHttpLink: true,
        },
      })
    );
  }

  // Execute all quarter queries in parallel
  const quarterResults = await Promise.allSettled(quarterQueryPromises);
  for (const quarterResult of quarterResults) {
    const calendarEvents = quarterResult?.value?.data?.node?.calendarEvents;
    if (!calendarEvents) {
      continue;
    }

    const updatedQuarterCalendarEvents = _.map(calendarEvents, event => {
      const { item } = event;
      if (
        userEntityType === STUDENT_USER &&
        event?.item?.__typename === "ProjectTaskResponse"
      ) {
        return {
          ...event,
          item: {
            ...item,
            deadline: item?.task,
          },
        };
      }
      return event;
    });
    mergedCalendarEvents.push(...(updatedQuarterCalendarEvents || []));
  }

  if (!_.isEmpty(currentQuarterData?.node)) {
    updateCalendarEventsCache({
      userId,
      userEntityType,
      selectedChildId,
      isUserParent,
      filters,
      startDate: academicStartDate,
      endDate: academicEndDate,
      statusWiseProjectFilters,
      calendarData: {
        node: {
          ...currentQuarterData?.node,
          calendarEvents: mergedCalendarEvents,
        },
      },
    });
  }
  return mergedCalendarEvents;
};

export const getCalendarEventDetailsFromCache = ({
  eventId,
  userId,
  userEntityType,
  filters,
  isUserParent,
  selectedChildId,
  eventDetailsVariables,
  isAgendaView = false,
  isUserStaff = false,
}) => {
  try {
    const queryData = client.readQuery({
      query: getCalendarEventDetailsQuery,
      variables: eventDetailsVariables,
    });

    const cachedCalendarEvent = _.get(queryData, "node", {});

    if (!_.isEmpty(cachedCalendarEvent)) {
      return cachedCalendarEvent;
    }
  } catch (e) {
    console.error("Error reading calendar event details from cache: ", e);
  }

  // Read from the same cache that useCalendarEvents populates (getCalendarEventsDataQuery)
  // using the EXACT same query variables to match the cache key
  try {
    // Extract and format dates exactly like useCalendarEvents does
    const { startDate, endDate } = filters || {};

    const queryData = client.readQuery({
      query: getCalendarEventsDataQuery,
      variables: {
        id: isUserParent ? selectedChildId : userId,
        type: isUserParent ? STUDENT_USER : userEntityType,
        filters: isAgendaView
          ? {
              ...filters,
              calendarIds: _.uniq(filters?.calendarIds || []),
            }
          : {
              ...filters,
              startDate: formatDate({
                date: getStartWeekOfMonth(startDate),
                format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
              }),
              endDate: formatDate({
                date: getEndWeekOfMonth(endDate),
                format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
              }),
              calendarIds: _.uniq(filters?.calendarIds || []),
            },
      },
    });

    // Find the specific event by ID from the cached list
    const calendarEvents = _.get(queryData, "node.calendarEvents", []);
    const cachedEvent = _.find(calendarEvents, { id: eventId });

    const isSchoolEventOrVideoMeeting = _.includes(
      ["VIDEO_MEETING", "SCHOOL_CALENDAR_EVENT"],
      _.get(cachedEvent, "item.eventType")
    );

    // for video meeting or school event we are not loading details in getCalendarEventsData query payload size getting exceed
    if (isUserStaff && isSchoolEventOrVideoMeeting) return {};

    return cachedEvent || {};
  } catch (e) {
    // Cache miss - query hasn't been executed yet or event not in the date range
    console.error("Error reading calendar event details from cache: ", e);
    return {};
  }
};

export const fetchRemainingQuartersAssignedData = async ({
  quartersForAcademicYear,
  currentQuarter,
  filters,
  userId,
  userEntityType,
  selectedChildId,
  isUserParent,
  associatedEntitiesFilters,
}) => {
  let mergedCalendarEvents = [];
  const {
    startDate: currentQuarterStartDate,
    endDate: currentQuarterEndDate,
  } = currentQuarter;

  const currentQuarterKey = `${currentQuarterStartDate}_${currentQuarterEndDate}`;

  // Read current quarter from cache
  const currentQuarterData = client.readQuery({
    query: getCalendarEventsParticipantDataQuery,
    variables: {
      id: isUserParent ? selectedChildId : userId,
      type: isUserParent ? STUDENT_USER : userEntityType,
      filters: {
        ...filters,
        startDate: getISOString(
          getDateMomentObject(currentQuarterStartDate).startOf("day")
        ),
        endDate: getISOString(
          getDateMomentObject(currentQuarterEndDate).endOf("day")
        ),
        calendarIds: _.uniq(filters?.calendarIds || []),
      },
      associatedEntitiesFilters,
    },
  });

  if (currentQuarterData?.node?.calendarEvents) {
    mergedCalendarEvents = [...currentQuarterData.node.calendarEvents];
  }

  const quarterQueryPromises = [];

  // Fetch remaining quarters
  for (const quarter of quartersForAcademicYear) {
    const quarterKey = `${quarter.startDate}_${quarter.endDate}`;

    if (quarterKey === currentQuarterKey) {
      continue;
    }

    const { startDate: quarterStartDate, endDate: quarterEndDate } = quarter;
    const startDate = getISOString(
      getDateMomentObject(quarterStartDate).startOf("day")
    );
    const endDate = getISOString(
      getDateMomentObject(quarterEndDate).endOf("day")
    );

    quarterQueryPromises.push(
      client.query({
        query: getCalendarEventsParticipantDataQuery,
        variables: {
          id: isUserParent ? selectedChildId : userId,
          type: isUserParent ? STUDENT_USER : userEntityType,
          filters: { ...filters, startDate, endDate },
          associatedEntitiesFilters,
        },
        fetchPolicy: "network-only",
        context: {
          useHttpLink: true,
        },
      })
    );
  }

  // Execute all quarter queries in parallel
  const quarterResults = await Promise.allSettled(quarterQueryPromises);
  for (const quarterResult of quarterResults) {
    const calendarEvents = quarterResult?.value?.data?.node?.calendarEvents;
    if (calendarEvents) {
      mergedCalendarEvents.push(...calendarEvents);
    }
  }

  return mergedCalendarEvents;
};
